import type Phaser from 'phaser';

import {
  animate,
  behind,
  dot,
  EARTH,
  EARTH_DARK,
  EARTH_LIGHT,
  ellipseDots,
  EMBER,
  fadeAfter,
  type Feet,
  GOLD,
  GOLD_TIP,
  groundRing,
  ORANGE,
  over,
  SOOT,
  WHITE,
} from './fx-kit.js';
import { puffSmoke } from './smoke-puff.js';

/**
 * The grimoire's blows on the ground (ADR 0048): cracks running, rocks
 * thrown up, shockwaves. Shared by the seismic wave, the titan's wrath and
 * the execute's landing.
 */

const TILE = 16;

/** A rock thrown up out of the ground, on a parabola, landing where it rose. */
interface Rock {
  x: number;
  y: number;
  drift: number;
  height: number;
  size: number;
  color: number;
}

function rockAt(x: number, y: number, spread: number): Rock {
  return {
    x: x + (Math.random() - 0.5) * spread,
    y: y - Math.random() * 3,
    drift: (Math.random() - 0.5) * 6,
    height: 5 + Math.random() * 8,
    size: Math.random() < 0.4 ? 2 : 1,
    color: [EARTH_LIGHT, EARTH, EARTH_DARK][Math.floor(Math.random() * 3)] ?? EARTH,
  };
}

function drawRock(graphics: Phaser.GameObjects.Graphics, rock: Rock, q: number): void {
  const x = Math.round(rock.x + rock.drift * q);
  const y = Math.round(rock.y - rock.height * 4 * q * (1 - q));
  graphics.fillStyle(rock.color, 1);
  graphics.fillRect(x, y, rock.size, rock.size);
}

/** A zigzag crack of the ground, `length` pixels along `angle`, drawn in up to `share` of it. */
export function crack(
  graphics: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  angle: number,
  length: number,
  share: number,
  seed: number,
): void {
  const steps = Math.round(length * share);
  let x = cx;
  let y = cy;
  for (let step = 0; step < steps; step += 1) {
    const wobble = ((seed * 31 + step * 17) % 5) - 2;
    x += Math.cos(angle);
    y += Math.sin(angle) * 0.5;
    const jag = step % 4 === 0 ? Math.sign(wobble) : 0;
    dot(graphics, x, y + jag, SOOT);
    if (step % 3 === 0) dot(graphics, x, y + jag - 1, EARTH_DARK);
  }
}

/**
 * Seismic wave: the ground splits tile after tile along the line, each tile
 * throwing up its rocks as the wave reaches it.
 */
export function seismic(scene: Phaser.Scene, caster: Feet, line: readonly Feet[]): void {
  puffSmoke(scene, { x: Math.round(caster.x), y: Math.round(caster.y) }, over(caster));
  line.forEach((tile, index) => {
    const cx = Math.round(tile.x);
    const cy = Math.round(tile.y) - 7;
    const rocks = Array.from({ length: 5 }, () => rockAt(cx, cy, 12));
    const angle = Math.random() * Math.PI;
    // Under whoever stands on the tile: the ground splits beneath them.
    animate(
      scene,
      behind(tile),
      560,
      (graphics, p) => {
        graphics.setAlpha(fadeAfter(p, 0.55));
        const share = Math.min(1, p / 0.3);
        crack(graphics, cx, cy, angle, 8, share, index);
        crack(graphics, cx, cy, angle + Math.PI, 8, share, index + 3);
        for (const rock of rocks) drawRock(graphics, rock, Math.min(1, p / 0.8));
        if (p < 0.2) {
          graphics.fillStyle(EARTH_LIGHT, 1);
          graphics.fillRect(cx - 3, cy - 1, 7, 2);
        }
      },
      index * 70,
    );
  });
}

/** An earth spike thrusting out of the ground, rising then sinking back. */
interface Spike {
  x: number;
  y: number;
  height: number;
  delayMs: number;
}

function drawSpike(graphics: Phaser.GameObjects.Graphics, spike: Spike, p: number): void {
  // Up fast, a beat at the top, back down slower.
  const rise = p < 0.25 ? p / 0.25 : p < 0.55 ? 1 : Math.max(0, 1 - (p - 0.55) / 0.45);
  const height = Math.round(spike.height * rise);
  const x = Math.round(spike.x);
  const y = Math.round(spike.y);
  for (let row = 0; row < height; row += 1) {
    // Wide at the foot, a point at the tip.
    const half = Math.max(0, Math.round(2 * (1 - row / spike.height)));
    for (let column = -half; column <= half; column += 1) {
      const color = column < 0 ? EARTH_LIGHT : column > 0 ? EARTH_DARK : EARTH;
      dot(graphics, x + column, y - row, row === height - 1 ? EARTH_LIGHT : color);
    }
  }
  if (height > 0) {
    graphics.fillStyle(SOOT, 1);
    graphics.fillRect(x - 3, y, 7, 1);
  }
}

/** A glowing fissure: molten while it opens, cooling to soot as it fades. */
function fissure(
  graphics: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  angle: number,
  length: number,
  share: number,
  heat: number,
  seed: number,
): void {
  const steps = Math.round(length * share);
  let x = cx;
  let y = cy;
  for (let step = 0; step < steps; step += 1) {
    const wobble = ((seed * 31 + step * 17) % 5) - 2;
    x += Math.cos(angle + wobble * 0.08);
    y += Math.sin(angle + wobble * 0.08) * 0.6;
    const jag = step % 4 === 0 ? Math.sign(wobble) : 0;
    // The glow runs behind the crack's head and cools from the caster outward.
    const hot = heat > step / Math.max(1, steps);
    dot(graphics, x, y + jag, hot ? (step % 3 === 0 ? GOLD : ORANGE) : SOOT);
    dot(graphics, x, y + jag + 1, hot ? EMBER : EARTH_DARK);
  }
}

/**
 * Titan's wrath: a pillar of light where the blow falls, then the ground
 * gives way out to the spell's reach: shockwaves, molten fissures running
 * off the caster's feet, earth spikes thrust up along the edge, rocks and
 * dust thrown high. The reach follows the spell's range, in tiles.
 */
export function titan(scene: Phaser.Scene, feet: Feet, reachTiles: number): void {
  const cx = Math.round(feet.x);
  const cy = Math.round(feet.y) - 3;
  const reach = reachTiles * TILE + 6;
  const flatten = 0.65;

  // The blow: a column of light drops onto the caster's feet.
  animate(scene, over(feet), 320, (graphics, p) => {
    graphics.setAlpha(fadeAfter(p, 0.3));
    const top = cy - Math.round(40 * Math.min(1, p / 0.15));
    const width = p < 0.2 ? 3 : 2;
    graphics.fillStyle(GOLD, 1);
    graphics.fillRect(cx - width - 1, top, width * 2 + 3, cy - top);
    graphics.fillStyle(GOLD_TIP, 1);
    graphics.fillRect(cx - width, top, width * 2 + 1, cy - top);
    graphics.fillStyle(WHITE, 1);
    graphics.fillRect(cx - 1, top, 3, cy - top);
    if (p < 0.25) graphics.fillRect(cx - 9, cy - 1, 19, 3);
  });

  // Three shockwaves, each thinner and wider than the last.
  for (let wave = 0; wave < 3; wave += 1) {
    const ring = (front: boolean) => (graphics: Phaser.GameObjects.Graphics, p: number) => {
      graphics.setAlpha(fadeAfter(p, 0.45));
      const eased = 1 - (1 - p) * (1 - p);
      const rx = 4 + eased * (reach + wave * 6);
      const ry = rx * flatten;
      const inner = wave === 0 ? (p < 0.3 ? WHITE : GOLD_TIP) : wave === 1 ? GOLD : EARTH_LIGHT;
      ellipseDots(graphics, cx, cy, rx, ry, front, (step) => (step % 3 === 0 ? ORANGE : inner));
      ellipseDots(graphics, cx, cy, rx - 1, ry - 1, front, () => (wave === 0 ? GOLD : EARTH));
      if (wave === 0) ellipseDots(graphics, cx, cy, rx - 2, ry - 1.5, front, () => EARTH_DARK);
    };
    groundRing(scene, feet, 760, ring, 60 + wave * 140);
  }

  // Molten fissures out towards the edge, uneven and forking, drawn under everyone.
  const fissures = Array.from({ length: 7 }, (_, index) => {
    const angle = (index / 7) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
    const length = (reach - 4) * (0.55 + Math.random() * 0.45);
    const fork = Math.random() < 0.5 ? -1 : 1;
    return { angle, length, forkAngle: angle + fork * (0.45 + Math.random() * 0.3) };
  });
  animate(
    scene,
    behind(feet),
    1500,
    (graphics, p) => {
      graphics.setAlpha(fadeAfter(p, 0.7));
      const heat = p < 0.4 ? 1 : Math.max(0, 1 - (p - 0.4) / 0.4);
      const run = Math.min(1, p / 0.25);
      fissures.forEach(({ angle, length, forkAngle }, index) => {
        fissure(graphics, cx, cy, angle, length, run, heat, index);
        // The fork splits off halfway once the crack has run past it.
        const forkShare = Math.max(0, (run - 0.5) / 0.5);
        if (forkShare > 0) {
          const fx = cx + Math.cos(angle) * length * 0.5;
          const fy = cy + Math.sin(angle) * length * 0.5 * 0.6;
          fissure(graphics, fx, fy, forkAngle, length * 0.4, forkShare, heat, index + 7);
        }
      });
      // The crater at the caster's feet.
      if (p < 0.8) {
        graphics.fillStyle(SOOT, 1);
        graphics.fillRect(cx - 4, cy - 1, 9, 3);
        graphics.fillStyle(p < 0.35 ? ORANGE : EARTH_DARK, 1);
        graphics.fillRect(cx - 2, cy, 5, 1);
      }
    },
    40,
  );

  // Earth spikes thrust up along the reach, each on its own depth so it stands among the bodies.
  const spikeCount = 4 + reachTiles * 3;
  for (let index = 0; index < spikeCount; index += 1) {
    const angle = (index / spikeCount) * Math.PI * 2 + Math.random() * 0.4;
    const distance = reach * (0.45 + Math.random() * 0.55);
    const spike: Spike = {
      x: cx + Math.cos(angle) * distance,
      y: cy + Math.sin(angle) * distance * flatten + 3,
      height: 9 + Math.round(Math.random() * 7),
      delayMs: 120 + (distance / reach) * 260,
    };
    animate(
      scene,
      spike.y + 1,
      700,
      (graphics, p) => {
        drawSpike(graphics, spike, p);
      },
      spike.delayMs,
    );
  }

  // Rocks thrown high, dust bursting at the edge.
  const rocks = Array.from({ length: 32 }, () => {
    const angle = Math.random() * Math.PI * 2;
    const distance = 4 + Math.random() * reach;
    const rock = rockAt(
      cx + Math.cos(angle) * distance,
      cy + Math.sin(angle) * distance * flatten,
      2,
    );
    return {
      ...rock,
      height: rock.height * 1.8,
      drift: rock.drift * 1.6,
      size: rock.size + (Math.random() < 0.25 ? 1 : 0),
    };
  });
  animate(scene, over(feet), 1000, (graphics, p) => {
    graphics.setAlpha(fadeAfter(p, 0.65));
    for (const rock of rocks) drawRock(graphics, rock, Math.min(1, p / 0.8));
  });
  puffSmoke(scene, { x: cx, y: Math.round(feet.y) }, over(feet));
  for (let index = 0; index < 6; index += 1) {
    const angle = (index / 6) * Math.PI * 2 + Math.random() * 0.5;
    const place = {
      x: Math.round(cx + Math.cos(angle) * reach * 0.85),
      y: Math.round(feet.y + Math.sin(angle) * reach * 0.85 * flatten),
    };
    scene.time.delayedCall(260 + index * 30, () => {
      puffSmoke(scene, place, place.y + 1);
    });
  }
}
